import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ChangeRequestDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { ChangeRequestRegisterWithUserFieldsRow } from '@risksmart-app/drizzle/src/dto';
import type { GetChangeRequestsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetChangeRequestsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { CHANGE_REQUEST_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

/**
 * Maps TRPC change request data to match the GraphQL query structure
 */
function mapChangeRequestsToGraphQl(
  trpcData: ChangeRequestRegisterWithUserFieldsRow[]
): StrictMappedGraphQLSelection<GetChangeRequestsQuery> {
  return {
    change_request: trpcData.map((item) => ({
      ...item,
      currentUserOwnerList: item.currentUserOwnerList || [],
      parentOwners: item.parentOwnerAndContributors || [],
      responses: item.responses.flatMap((r) =>
        r.approver ? [{ ...r, approver: r.approver }] : []
      ),
      parent: item.parent
        ? {
            ...item.parent,
            documentFile: item.document_file
              ? {
                  Version: item.document_file.Version,
                  parent: item.document_file.parent,
                }
              : null,
            acceptance: item.acceptance,
            risk: item.risk,
            control: item.control,
            action: item.action,
            issue_assessment: item.issue_assessment,
          }
        : null,
    })),
  };
}

/**
 * COI-691's single non-additive change: `currentUserId` was a query arg used
 * only to fill the GraphQL variable, which forced every caller to pass ambient
 * context. It is resolved from `useRisksmartUser()` here instead, so callers
 * pass dashboard filters only.
 */
const useChangeRequestsGraphqlVariables = (args: UseGetChangeRequestsArgs) => {
  const { user } = useRisksmartUser();

  const { where } = buildDashboardWhere({
    filters: args,
    datePredicates: CHANGE_REQUEST_DATE_PREDICATES,
  });

  return {
    currentUserId: user?.userId ?? '',
    ...(where ? { where } : {}),
  };
};

/**
 * Change request declares no tag or department filter, and the dashboard's
 * eight ownership flags have no server leg here — the register's actionability
 * narrowing is the client-side `isActiveApprover` pass instead. So the
 * dashboard half is dates only.
 *
 * "Client-side" is about which readable rows the dashboard chooses to surface,
 * NOT about who may read them. The row set itself is bounded server-side by
 * perm_v2 + RLS on the parent entity (`getChangeRequestsRegisterProcessor`);
 * no argument to this hook can widen it.
 */
export type UseGetChangeRequestsArgs = Pick<
  DashboardFilterArgs<ChangeRequestDashboardDateField>,
  'dateRange'
>;

export const useGetChangeRequests = createQueryHook<
  UseGetChangeRequestsArgs,
  ChangeRequestRegisterWithUserFieldsRow[],
  GetChangeRequestsQuery
>({
  trpcQueryOptions: (trpc, { dateRange }) =>
    trpc.frontend.changeRequest.register.queryOptions(
      toTrpcDashboardFilterInput({ dateRange })
    ),
  mapTrpcDataToGraphQL: mapChangeRequestsToGraphQl,
  graphqlDocument: GetChangeRequestsDocument,
  graphqlVariables: useChangeRequestsGraphqlVariables,
});
